import { Call, MissedVideoCall, Videocam } from "@mui/icons-material";
import { Box, Button, Checkbox, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { AvatarGroup } from "components";
import { AudioEnd, IncomingAudioCall, MissedCallIcon } from "components/icons";
import { KeyConstant, SystemConstant } from "const";
import InitGroupCallPopup from "pages/HomePage/components/MessengerChat/InitGroupCallPopup";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CallingAction from "redux-store/calling.redux";
import { StorageUtil, toCamel, uuid } from "utils";
import { getDisplayTimestamp } from "utils/date.utils";
import { isGroupOrChannelType } from "utils/view.utils";
import { useCallingActions } from "../../../../../../ring-rtc/state/ducks/calling";
import {
  mapStateToActiveCallProp,
  mapStateToIncomingCallProp,
} from "../../../../../../ring-rtc/state/smart/CallManager";
const callingUtil = window.electronLibs.ringRtc.callingUtil;

const CallHistoryItem = ({ callHistory = {}, onSelect, checked }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const isVoiceCall = callHistory.type === SystemConstant.CALL_HISTORY_TYPE.audioCall;
  const isMissedCall =
    callHistory.status === SystemConstant.CALL_HISTORY_STATUS.missed && callHistory.senderId !== accountId;
  const callType = getCallType(callHistory);
  const activeCall = useSelector(mapStateToActiveCallProp);
  const incomingCall = useSelector(mapStateToIncomingCallProp);

  const [isOpenInitGroupCall, setIsOpenInitGroupCall] = useState(false);
  const { onOutgoingAudioCallInConversation, onOutgoingVideoCallInConversation } = useCallingActions();

  const handleCall = () => {
    if (isGroupOrChannelType(callHistory.groupDetail.groupType)) {
      setIsOpenInitGroupCall(true);
    } else {
      const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
      const groupDetail = callHistory.groupDetail;

      const callingGroupDetail = toCamel({ ...groupDetail, room_id: uuid() });
      callingUtil.createCallingInstance();
      dispatch(
        CallingAction.callingSet({
          callingGroupDetailRing: callingGroupDetail,
        }),
      );

      const prefixKey = StorageUtil.getCurrentPrefixKey();
      const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
      const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
      if (!isVoiceCall) {
        onOutgoingVideoCallInConversation(
          callingGroupDetail?.id,
          accountId,
          branchId,
          deviceId,
          callingGroupDetail.roomId,
        );
      } else {
        onOutgoingAudioCallInConversation(
          callingGroupDetail?.id,
          accountId,
          branchId,
          deviceId,
          callingGroupDetail.roomId,
        );
      }
    }
  };

  return (
    <Stack direction="row" alignItems="center" p="8px 16px">
      <Checkbox edge="start" onChange={onSelect} checked={checked} />

      <Stack direction="row" alignItems="center" width="100%">
        <AvatarGroup
          group={{
            groupType: callHistory.groupDetail.groupType,
            groupName: callHistory.groupDetail.groupName,
            privateF: callHistory.groupDetail.privateF,
          }}
          avatarId={callHistory.groupDetail.avatarId}
        />
        <Box ml={1}>
          <Typography className="ellipsis">{callHistory.groupDetail.groupName}</Typography>
          <CallHistoryIcon
            type={callType}
            content={getDisplayTimestamp(callHistory.timeStart)}
            className={isMissedCall && classes.missedCallIcon}
          />
        </Box>
      </Stack>
      <Button
        variant="contained"
        className={classes.callButton}
        classes={{ root: classes.rootButton, disabled: classes.disabledButton }}
        disabled={Boolean(activeCall) || Boolean(incomingCall)}
        onClick={handleCall}
      >
        {isVoiceCall ? <Call color="white" /> : <Videocam color="white" />}
      </Button>

      {isOpenInitGroupCall && (
        <InitGroupCallPopup data={callHistory.groupDetail} open onClose={() => setIsOpenInitGroupCall(false)} />
      )}
    </Stack>
  );
};

export default CallHistoryItem;

const CALL_HISTORY_TYPE = {
  audioEnd: 1,
  incomingAudioEnd: 2,
  audioMissed: 3,
  video: 4,
};

const getCallType = callHistory => {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
  const isIncomingCall = callHistory.senderId !== accountId;
  const isAudioCall = callHistory.type === SystemConstant.CALL_HISTORY_TYPE.audioCall;

  const isMissesAudioCall = isAudioCall && callHistory.status === SystemConstant.CALL_HISTORY_STATUS.missed;
  const isIncomingAudioCall =
    isAudioCall && isIncomingCall && callHistory.status === SystemConstant.CALL_HISTORY_STATUS.end;
  const isAudioCallEnd =
    isAudioCall && !isIncomingCall && callHistory.status === SystemConstant.CALL_HISTORY_STATUS.end;

  let callType;
  switch (true) {
    case !isAudioCall:
      callType = CALL_HISTORY_TYPE.video;
      break;

    case isMissesAudioCall:
      callType = CALL_HISTORY_TYPE.audioMissed;
      break;

    case isIncomingAudioCall:
      callType = CALL_HISTORY_TYPE.incomingAudioEnd;
      break;

    case isAudioCallEnd:
      callType = CALL_HISTORY_TYPE.audioEnd;
      break;

    default:
      callType = CALL_HISTORY_TYPE.audioEnd;
      break;
  }

  return callType;
};

const CallHistoryIcon = ({ type, content, className }) => {
  const classes = useStyles();

  let Icon = null;
  switch (type) {
    case CALL_HISTORY_TYPE.audioEnd:
      Icon = <AudioEnd sx={{ fontSize: "1rem", margin: "4px" }} />;
      break;

    case CALL_HISTORY_TYPE.incomingAudioEnd:
      Icon = <IncomingAudioCall sx={{ fontSize: "1rem", margin: "4px" }} />;
      break;
    case CALL_HISTORY_TYPE.audioMissed:
      Icon = <MissedCallIcon sx={{ fontSize: "1rem", margin: "4px" }} />;
      break;

    case CALL_HISTORY_TYPE.video:
      Icon = <MissedVideoCall />;
      break;

    default:
      break;
  }

  return (
    <Box className={clsx(classes.iconWrapper, className)}>
      <Box className={classes.icon}>{Icon}</Box>
      <Typography className={clsx(classes.lastMessage, "ellipsis", "regular-md-txt", className)}>{content}</Typography>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  disabledButton: {
    backgroundColor: "#72B9E6 !important",
    color: theme.palette.white + "!important",
  },

  rootButton: {
    padding: 0,
    minWidth: 42,
    minHeight: 30,
  },

  missedCallIcon: {
    color: "#EB544B !important",
  },

  iconWrapper: {
    color: theme.palette.grey[500],
    display: "flex",
    alignItems: "center",
    borderRadius: "50%",
    "&>svg": {
      marginRight: 0,
    },
  },

  lastMessage: {
    lineHeight: "1.125rem",
    whiteSpace: "break-spaces",
  },

  icon: {
    width: 20,
    height: 20,
    marginRight: 5,
  },
}));
